import { type CSSProperties, useState } from 'react';
import { clsx } from 'clsx';
import { Button, IconButton, Tooltip, Popover, OpacityInput } from '@nula/core-ui';
import type { FormattingState, StrokeStyle } from '@/features/canvas/engine/hooks/use-formatting-state';
import { ColorSwatchGrid } from './color-swatch-popover';
import styles from './fill-border-group.module.css';
// Note: file renamed from FillBorderPopover → FillBorderGroup so it lines
// up with the FontGroup / TextStyleGroup / AlignGroup naming pattern in
// this folder. The component still hosts its UI inside a popover — the
// `Group` suffix matches its peers, not the rendering shape.

type ColorTab = 'fill' | 'border';

const STROKE_STYLES: { value: StrokeStyle; label: string }[] = [
  { value: 'solid',  label: 'Solid'  },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
];

export interface FillBorderGroupProps {
  state: FormattingState;
  /** Override label for the trigger tooltip. Defaults to 'Color & Border'. */
  label?: string;
  /** Hide the stroke-style (solid/dashed/dotted) row under the Border tab.
   *  Use for node kinds whose stroke style is fixed (e.g. frames currently
   *  always render solid). Default false — full surface like Shape. */
  hideStrokeStyle?: boolean;
}

/**
 * Reusable Fill/Border tabbed popover. Drives both `state.fillColor +
 * fillOpacity` and `state.strokeColor + strokeOpacity` (plus `strokeStyle`
 * unless `hideStrokeStyle`) from a single trigger swatch.
 *
 * Used by every node kind that exposes a merged color surface — Sticky,
 * Shape, Frame. Shape only previously inlined this; extracted so frames
 * can compose the same primitive without duplicating the tabs/grid logic.
 */
export function FillBorderGroup({
  state,
  label = 'Color & Border',
  hideStrokeStyle = false,
}: FillBorderGroupProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ColorTab>('fill');

  const rawFill = state.fillColor === 'none' ? '#FFFFFF' : state.fillColor;
  const activeColor = tab === 'fill' ? rawFill : state.strokeColor;
  const activeOpacity = tab === 'fill' ? state.fillOpacity : state.strokeOpacity;
  const onActiveChange = tab === 'fill' ? state.setFillColor : state.setStrokeColor;
  const onActiveOpacity = tab === 'fill' ? state.setFillOpacity : state.setStrokeOpacity;

  // Trigger swatch reflects both fill (interior) and stroke (border) so the
  // user can see both current colors at a glance without opening the popover.
  const triggerFill = state.fillColor === 'none' ? 'transparent' : state.fillColor;
  const triggerStroke = state.strokeColor === 'none' ? 'transparent' : state.strokeColor;

  const content = (
    <div className={styles.popover}>
      <div className={styles.tabs}>
        <Button
          variant="text"
          size="sm"
          className={clsx(styles.tab, tab === 'fill' && styles.tabActive)}
          onClick={() => setTab('fill')}
        >
          Fill
        </Button>
        <Button
          variant="text"
          size="sm"
          className={clsx(styles.tab, tab === 'border' && styles.tabActive)}
          onClick={() => setTab('border')}
        >
          Border
        </Button>
      </div>
      <OpacityInput value={activeOpacity} onChange={onActiveOpacity} color={activeColor} />
      <ColorSwatchGrid value={activeColor} onChange={onActiveChange} />
      {tab === 'border' && !hideStrokeStyle && (
        <div className={styles.strokeStyleRow}>
          {STROKE_STYLES.map(({ value, label: styleLabel }) => (
            <Tooltip key={value} title={styleLabel} placement="top">
              <IconButton
                transparent
                aria-label={styleLabel}
                aria-pressed={state.strokeStyle === value}
                className={clsx(styles.strokeStyleBtn, state.strokeStyle === value && styles.strokeStyleActive)}
                onClick={() => state.setStrokeStyle(value)}
              >
                <span className={clsx(styles.strokePreview, value !== 'solid' && styles[`stroke_${value}`])} />
              </IconButton>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title={label} placement="top" open={open ? false : undefined}>
        <IconButton transparent aria-label={label} className={styles.swatchBtn}>
          <span
            className={clsx(styles.swatchDot, styles.swatchDotMerged)}
            style={{ '--swatch-color': triggerFill, '--swatch-stroke': triggerStroke } as CSSProperties}
          />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}
